
import React from 'react';
import { useCostEstimator } from '@/context/CostEstimatorContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { UsersIcon } from 'lucide-react';

const UserVolumeStep: React.FC = () => {
  const { setUserVolume, options, goToNextStep, goToPreviousStep, COSTS } = useCostEstimator();
  
  const volumeOptions = [
    { value: 'small', label: 'Small (Up to 100 users)', cost: COSTS.userVolume.small },
    { value: 'medium', label: 'Medium (Up to 1,000 users)', cost: COSTS.userVolume.medium },
    { value: 'large', label: 'Large (Up to 100,000 users)', cost: COSTS.userVolume.large },
    { value: 'enterprise', label: 'Enterprise (100,000+ users)', cost: COSTS.userVolume.enterprise },
  ];

  const volumeIndex = options.userVolume ? 
    volumeOptions.findIndex(option => option.value === options.userVolume) : 
    0;

  const handleSliderChange = (value: number[]) => {
    const index = value[0];
    setUserVolume(volumeOptions[index].value as any);
  };

  return (
    <Card className="form-card">
      <CardHeader>
        <CardTitle>How many users do you expect?</CardTitle>
        <CardDescription>Select the expected user volume for your application</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-8 mt-4">
          <div className="mb-10">
            <Slider 
              defaultValue={[volumeIndex]} 
              max={3} 
              step={1} 
              onValueChange={handleSliderChange}
            />
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-center mb-6">
            {volumeOptions.map((option, index) => (
              <div 
                key={option.value}
                className={`${volumeIndex === index ? 'text-primary font-medium' : 'text-muted-foreground'}`}
              >
                <div className="flex flex-col items-center">
                  <UsersIcon className="h-5 w-5 mb-1" />
                  <span className="text-sm">{option.label.split(' ')[0]}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-muted rounded-lg p-4 flex justify-between items-center">
            <div>
              <Label>Selected volume:</Label>
              <p className="text-lg font-medium">
                {options.userVolume ? volumeOptions.find(o => o.value === options.userVolume)?.label : 'Small (Up to 100 users)'}
              </p>
            </div>
            <div className="text-right">
              <Label>Additional cost:</Label>
              <p className="text-lg font-medium text-primary">
                ${options.userVolume ? COSTS.userVolume[options.userVolume].toLocaleString() : '0'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between">
          <Button variant="outline" onClick={goToPreviousStep}>
            Previous
          </Button>
          <Button onClick={goToNextStep}>
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserVolumeStep;
